import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Layers, AlertTriangle, CornerLeftUp } from 'lucide-react';

const shortId = (id) => (id || '').slice(0, 8);

export const AssemblyBuildsCard = ({ wo }) => {
  const { api } = useAuth();
  const [children, setChildren] = useState({});
  const used = wo.assembly_revisions_used || [];
  const warnings = wo.assembly_warnings || [];

  useEffect(() => {
    if (!used.length) return;
    api().get(`/work-orders/${wo.id}/family-status`)
      .then(r => setChildren(Object.fromEntries((r.data.children || []).map(c => [c.id, c]))))
      .catch(() => {});
  }, [api, wo.id, used.length]);

  if (!used.length && !warnings.length && !wo.parent_work_order_id && !wo.assembly_received) return null;

  return (
    <Card className="industrial-card" data-testid="wo-assembly-card">
      <CardHeader className="border-b border-slate-100 pb-3">
        <CardTitle className="text-base flex items-center gap-2"><Layers className="w-4 h-4 text-sky-600" /> Assemblies &amp; Traceability</CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {wo.parent_work_order_id && (
          <Link to={`/work-orders/${wo.parent_work_order_id}`} className="text-sm text-orange-600 hover:underline inline-flex items-center gap-1" data-testid="wo-parent-link">
            <CornerLeftUp className="w-4 h-4" /> Builds into parent WO {shortId(wo.parent_work_order_id)}
          </Link>
        )}
        {wo.assembly_received && (
          <p className="text-sm text-emerald-700" data-testid="wo-assembly-received">Added {wo.assembly_received.qty} × <span className="font-mono">{wo.assembly_received.sku}</span> to stock on completion.</p>
        )}
        {warnings.map((w, i) => (
          <p key={i} className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 flex gap-1" data-testid={`wo-assembly-warning-${i}`}><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{w}</p>
        ))}
        {used.length > 0 && (
          <table className="w-full text-xs" data-testid="wo-assembly-table">
            <thead className="text-slate-500 text-left">
              <tr><th className="py-1">Assembly</th><th>Revision used</th><th>Needed</th><th>On hand at start</th><th>Child WOs</th></tr>
            </thead>
            <tbody>
              {used.map(u => (
                <tr key={u.component_sku} className="border-t border-slate-100" data-testid={`wo-assembly-row-${u.component_sku}`}>
                  <td className="py-1.5"><div className="font-medium text-slate-800">{u.assembly_name}</div><div className="font-mono text-slate-500">{u.component_sku}</div></td>
                  <td>{u.bom_id ? <Link to={`/boms/view/${u.bom_id}`} className="font-mono text-orange-600 hover:underline">rev {u.bom_revision}</Link> : <span className="text-amber-700">none released</span>}</td>
                  <td className="font-mono">{u.required_qty}</td>
                  <td className="font-mono">{u.on_hand_at_creation}</td>
                  <td>
                    {u.child_work_order_ids.length === 0 ? <span className="text-slate-400">from stock</span> : u.child_work_order_ids.map(cid => (
                      <Link key={cid} to={`/work-orders/${cid}`} className="inline-block mr-1 mb-0.5 font-mono px-1.5 py-0.5 rounded border border-slate-200 hover:bg-slate-50" data-testid={`wo-child-link-${cid}`}>
                        {children[cid]?.work_order_number || shortId(cid)} · {children[cid]?.status || '…'}
                      </Link>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
};
